import _ from "lodash";
import { FilterOperators, QueryBuilderContext } from "../types/enums";
import { textToTsQueryText } from "./helpers";

type QueryConjuction = "AND" | "OR";
type QueryRule = { field: string; operator: string; value: any };
type NodeRules = Array<{ rule: QueryRule } | { group: { conjunction: QueryConjuction; nodes: NodeRules } }>;
type FilterQuery = { conjunction: QueryConjuction; nodes: NodeRules; not?: boolean | undefined };

export default class SearchQueryBuilder {
  private queryBuilderContext: QueryBuilderContext;

  constructor(context: QueryBuilderContext) {
    this.queryBuilderContext = context;
  }

  private buildClauseQuery(values: Record<string, any>): FilterQuery {
    const {
      keywordSearchText,
      searchText,
      tagsIds,
      excludeClauses,
      isBooleanSearch,
      documentTitle,
      FilterTagCategories,
    } = values;
    const nodes: NodeRules = [];
    const primaryTextValues = [searchText] as string[];

    if (isBooleanSearch) {
      nodes.push({
        rule: {
          field: "keywords",
          operator: FilterOperators.EQUAL,
          value: textToTsQueryText(keywordSearchText.trim()),
        } as QueryRule,
      });
    } else {
      nodes.push({
        rule: {
          field: "primaryText",
          operator: FilterOperators.EQUAL,
          value: primaryTextValues,
        } as QueryRule,
      });
    }

    if (documentTitle) {
      nodes.push({
        rule: { field: "documentTitle", operator: FilterOperators.CONTAINS, value: documentTitle } as QueryRule,
      });
    }

    if (excludeClauses instanceof Array && excludeClauses.length) {
      const badSugGroups: NodeRules = [];
      badSugGroups.push({
        rule: {
          field: "clauseId",
          operator: FilterOperators.NOT_IN,
          value: excludeClauses,
        },
      });
      nodes.push({
        group: {
          conjunction: "AND",
          nodes: badSugGroups,
        },
      });
    }

    if (tagsIds && tagsIds instanceof Array && tagsIds.length) {
      const tagGroups: NodeRules = [];
      FilterTagCategories?.forEach((category) => {
        if (Array.isArray(category) && category.length > 0) {
          tagGroups.push({
            rule: {
              field: "tagIds",
              operator: FilterOperators.ARRAY_OVERLAPS,
              value: category,
            } as QueryRule,
          });
        }
      });

      if (tagGroups.length) {
        nodes.push({
          group: {
            conjunction: "AND",
            nodes: tagGroups,
          },
        });
      }
    }

    return { conjunction: "AND", nodes };
  }

  getQueryField<T>(value: FilterQuery, constraint: [string, string], field: string): T {
    const result = value.nodes.find((v) => _.get(v, constraint[0]) === constraint[1]);
    return _.get(result, field) as T;
  }

  buildQuery(values: Record<string, any>): FilterQuery {
    switch (this.queryBuilderContext) {
      case QueryBuilderContext.CLAUSE_SEARCH: {
        return this.buildClauseQuery(values);
      }
      default: {
        return { conjunction: "AND", nodes: [] };
      }
    }
  }
}
